import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Target, Camera, Star, Eye, Shield, Zap, Globe, TreePine, Moon } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { useHydrated } from '@/hooks/use-hydrated';

const LightPollutionMap = lazy(() => import('@/components/map/LightPollutionMap'));

const FAQ_ITEMS = [
  {
    question: 'What is the Bortle scale?',
    answer:
      'The Bortle scale is a nine-level numeric scale that measures the night sky brightness at a particular location. Class 1 represents the darkest skies possible on Earth, while Class 9 represents inner-city skies where only the Moon, planets, and a few bright stars are visible. It was created by amateur astronomer John Bortle in 2001 and published in Sky & Telescope magazine.',
  },
  {
    question: 'What is light pollution?',
    answer:
      'Light pollution is the excessive or misdirected artificial light produced by human activity. It includes skyglow (brightening of the night sky), glare (excessive brightness causing discomfort), light trespass (unwanted light), and clutter (excessive groupings of light). More than 80% of the world\'s population lives under light-polluted skies.',
  },
  {
    question: 'Where are the darkest skies in the United States?',
    answer:
      'The darkest skies in the US are found in remote areas of the Southwest (Utah, Nevada, Arizona), Big Bend in Texas, central Idaho, and parts of rural Montana and Wyoming. Many of these areas are designated International Dark Sky Parks or Reserves. Cosmic Campground in New Mexico and Natural Bridges in Utah are among the very darkest.',
  },
  {
    question: 'How does light pollution affect astrophotography?',
    answer:
      'Light pollution creates a bright glow in the sky that washes out faint celestial objects like the Milky Way, nebulae, and galaxies. Even moderate light pollution (Bortle 5-6) can make the Milky Way invisible to the naked eye and significantly reduce contrast in long-exposure photographs. Moving from Bortle 5 to Bortle 3 can be the difference between a washed-out image and a stunning Milky Way photograph.',
  },
  {
    question: 'Can I photograph the Milky Way from a city?',
    answer:
      'The Milky Way core is essentially invisible from cities (Bortle 7-9). For Milky Way photography, you need at least Bortle 4 or darker skies. However, you can photograph the Moon, bright planets, star trails, and some bright nebulae from urban locations using narrowband filters that block artificial light.',
  },
  {
    question: 'What is the best time of year to see the Milky Way?',
    answer:
      'In the Northern Hemisphere, the Milky Way core is best visible from February through October, with peak visibility from May through August when the galactic center rises highest in the sky. The best nights are those near the new moon when moonlight does not wash out the sky. Use our Milky Way Planner to find the exact best nights for your location.',
  },
  {
    question: 'How far do I need to travel to escape light pollution?',
    answer:
      'It depends on the size of nearby cities. Generally, you need to be 50-100 miles from a major metropolitan area to reach Bortle 3-4 skies. Light domes from large cities can be visible on the horizon even 150+ miles away, but they typically do not affect overhead sky quality at that distance. Use the light pollution map on this page to identify dark corridors within driving distance.',
  },
  {
    question: 'What is an International Dark Sky Park?',
    answer:
      'An International Dark Sky Park is a designation given by DarkSky International (formerly the International Dark-Sky Association) to public or private land that has exceptional night sky quality and implements responsible lighting policies. These parks commit to preserving their dark skies through ordinances, education, and community engagement. The US has more certified dark sky places than any other country.',
  },
  {
    question: 'Is light pollution getting worse?',
    answer:
      'Yes. Global light pollution is increasing by approximately 2% per year, driven by urban sprawl, cheaper LED lighting, and the tendency to add more light when energy costs decrease. Satellite measurements show that artificially lit outdoor areas grew by 2.2% per year between 2012 and 2016, and the trend has continued. However, many communities are adopting dark-sky-friendly lighting ordinances that demonstrate the problem is solvable.',
  },
  {
    question: 'How can I reduce light pollution?',
    answer:
      'Use fully shielded outdoor fixtures that direct light downward. Choose warm-colored LEDs (2700K or lower) instead of blue-white lights. Turn off unnecessary outdoor lights. Advocate for dark-sky-friendly lighting codes in your community. Support certified Dark Sky Places and DarkSky International. Even small changes at home contribute to reducing skyglow in your neighborhood.',
  },
];

const BORTLE_CLASSES = [
  { bortle: 1, color: '#000000', textColor: 'white', name: 'Excellent Dark Sky', description: 'Zodiacal light, gegenschein, and zodiacal band visible. The Milky Way casts obvious shadows. Airglow is readily apparent.' },
  { bortle: 2, color: '#333333', textColor: 'white', name: 'Typical Dark Site', description: 'Airglow may be weakly apparent. The Milky Way is highly structured and appears as a striking feature. M33 is visible with direct vision.' },
  { bortle: 3, color: '#0000CC', textColor: 'white', name: 'Rural Sky', description: 'Some light pollution evident at the horizon. Clouds are illuminated near the horizon but dark overhead. The Milky Way appears wide and detailed.' },
  { bortle: 4, color: '#007099', textColor: 'white', name: 'Rural/Suburban Transition', description: 'Light pollution domes visible over population centers. The Milky Way is still impressive but lacks fine detail. M33 is difficult without averted vision.' },
  { bortle: 5, color: '#00CC00', textColor: 'black', name: 'Suburban Sky', description: 'The Milky Way is weak or invisible near the horizon. Clouds are noticeably brighter than the sky background. Only hints of zodiacal light on the best nights.' },
  { bortle: 6, color: '#99CC00', textColor: 'black', name: 'Bright Suburban', description: 'The Milky Way is only apparent overhead and appears washed out. Light domes are visible in most directions. Sky within 35 degrees of the horizon glows grayish white.' },
  { bortle: 7, color: '#FFFF00', textColor: 'black', name: 'Suburban/Urban Transition', description: 'The entire sky has a grayish-white hue. The Milky Way is barely visible if at all. Strong light sources are evident in all directions.' },
  { bortle: 8, color: '#FF8800', textColor: 'black', name: 'City Sky', description: 'The sky glows white or orange. You can easily read a newspaper by the sky glow. Only bright stars, planets, and the Moon are visible.' },
  { bortle: 9, color: '#FF0000', textColor: 'white', name: 'Inner-City Sky', description: 'The entire sky is brightly lit. Many stars making up familiar constellations are invisible. Only the Moon, planets, and a few of the brightest stars can be seen.' },
];

const FEATURED_LOCATIONS = [
  { name: 'Big Bend National Park', slug: 'big-bend-international-dark-sky-park', state: 'Texas', bortle: 2 },
  { name: 'Cherry Springs State Park', slug: 'cherry-springs-dark-sky-park', state: 'Pennsylvania', bortle: 2 },
  { name: 'Death Valley National Park', slug: 'death-valley-national-park-international-dark-sky-park', state: 'California', bortle: 2 },
  { name: 'Natural Bridges National Monument', slug: 'natural-bridges-national-monument-dark-sky-park', state: 'Utah', bortle: 2 },
  { name: 'Headlands International Dark Sky Park', slug: 'headlands-international-dark-sky-park', state: 'Michigan', bortle: 3 },
  { name: 'Cosmic Campground', slug: 'cosmic-campground-international-dark-sky-sanctuary', state: 'New Mexico', bortle: 1 },
];

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const datasetStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Dataset',
  name: 'US Light Pollution Map',
  description:
    'Interactive light pollution map showing Bortle scale sky brightness across the United States, derived from satellite radiance data.',
  temporalCoverage: '2024',
  spatialCoverage: {
    '@type': 'Place',
    name: 'United States',
  },
  creator: {
    '@type': 'Organization',
    name: 'Milky Way Planner',
    url: 'https://www.milkywayplanner.com',
  },
};

export default function LightPollution() {
  const hydrated = useHydrated();

  return (
    <>
      <SEOHead
        title="Light Pollution Map — Dark Skies for Astrophotography"
        description="Interactive light pollution map showing Bortle scale sky brightness across the US. Find dark sky locations for Milky Way photography and stargazing."
        keywords="light pollution map, bortle scale map, dark sky map, dark sky finder, light pollution overlay, night sky brightness, stargazing locations, astrophotography locations, bortle class"
        canonicalUrl="/light-pollution"
        structuredData={[faqStructuredData, datasetStructuredData]}
      />

      {/* Hero */}
      <div className="bg-[#0a0e1a] text-white py-16 sm:py-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gold text-sm font-semibold tracking-widest uppercase mb-4">
            Light Pollution Map
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight">
            Find Dark Skies for Stargazing{' '}
            <span className="text-gold">&amp; Astrophotography</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Light pollution affects over 80% of the world's population. Use this
            interactive map to find the darkest skies near you and plan your
            next night under the stars.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-16 py-12">

        {/* Map */}
        <section>
          {hydrated ? (
            <Suspense
              fallback={
                <div className="h-[min(600px,65vh)] rounded-xl bg-muted animate-pulse" />
              }
            >
              <LightPollutionMap />
            </Suspense>
          ) : (
            <div className="h-[min(600px,65vh)] rounded-xl bg-muted animate-pulse" />
          )}
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Zoom and pan to explore light pollution levels. Data derived from
            satellite radiance observations (ALR v2).
          </p>
        </section>

        {/* What Is Light Pollution */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-4">
            What Is Light Pollution?
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              Light pollution is the excessive or misdirected artificial light produced by human activity. It brightens the night sky over populated areas, obscuring stars and celestial objects that have been visible to every generation of humans until the last century. Today, more than 80% of the world's population lives under light-polluted skies, and a third of humanity can no longer see the Milky Way.
            </p>
            <p>
              There are four main types of light pollution: <strong>skyglow</strong>, the brightening of the night sky over inhabited areas; <strong>glare</strong>, excessive brightness that causes visual discomfort; <strong>light trespass</strong>, light falling where it is not intended or needed; and <strong>clutter</strong>, bright, confusing, and excessive groupings of light sources. All four contribute to the degradation of the night environment.
            </p>
            <p>
              The map above visualizes skyglow across the United States using the <Link to="#bortle-scale" className="text-gold hover:text-gold-light font-medium">Bortle scale</Link>, derived from satellite radiance measurements. Brighter colors indicate heavier light pollution. The darkest areas, shown in black, represent the increasingly rare places where truly pristine night skies still exist.
            </p>
          </div>
        </section>

        {/* The Problem with Light Pollution */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-4">
            Why Light Pollution Is a Growing Problem
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4 mb-10">
            <p>
              Global light pollution is increasing by an estimated 2% per year. As cities expand and LED lighting becomes cheaper, the night sky is disappearing faster than at any point in human history. This has consequences far beyond stargazing.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl border border-border bg-card">
              <Shield className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Ecological Damage</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Artificial light at night disrupts ecosystems on a massive scale. Sea turtle hatchlings become disoriented by coastal lighting and wander inland instead of toward the ocean. Migratory birds, which navigate by starlight, collide with illuminated buildings by the hundreds of millions each year. Insects, the foundation of many food chains, are drawn to artificial lights and die of exhaustion. Pollination patterns shift. Predator-prey relationships are altered. Over 60% of invertebrates and 30% of vertebrates are nocturnal, and artificial light fundamentally changes their world.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-card">
              <Eye className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Human Health</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Exposure to artificial light at night suppresses melatonin production, the hormone that regulates sleep-wake cycles. Chronic disruption of circadian rhythms is associated with increased risk of obesity, depression, diabetes, and certain cancers. The American Medical Association has recognized light pollution as a public health concern. Blue-rich LED streetlights, while energy-efficient, are particularly disruptive to melatonin production and sleep quality.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-card">
              <Zap className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Wasted Energy</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                An estimated 30% of outdoor lighting in the United States is wasted, meaning light is directed upward or sideways where it serves no purpose. This waste costs over $3 billion per year in the US alone and produces millions of tons of carbon dioxide emissions. Properly shielded, downward-facing fixtures can reduce light pollution while maintaining or improving visibility on the ground.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-card">
              <Star className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-semibold text-foreground mb-2">Cultural Loss</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For thousands of years, the night sky shaped human culture, navigation, agriculture, religion, and storytelling. The Milky Way is central to the mythologies of cultures worldwide. Today, most children growing up in cities and suburbs will never see the Milky Way with their own eyes. Losing access to the night sky severs a connection to the natural world that every prior generation shared.
              </p>
            </div>
          </div>
        </section>

        {/* What Are Dark Skies */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-4">
            What Are Dark Skies?
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              "Dark skies" refers to areas where artificial light pollution is minimal or absent, allowing the natural night sky to be seen in its full splendor. In a truly dark sky location (Bortle Class 1-2), you can see the Milky Way stretching across the entire sky, thousands of stars visible to the naked eye, zodiacal light along the ecliptic, and even the faint glow of airglow in the upper atmosphere.
            </p>
            <p>
              The <strong>International Dark-Sky Association (DarkSky International)</strong> certifies locations that demonstrate exceptional night sky quality and a commitment to responsible lighting. These designations include <strong>Dark Sky Parks</strong>, <strong>Dark Sky Reserves</strong>, <strong>Dark Sky Sanctuaries</strong>, and <strong>Dark Sky Communities</strong>. The US is home to more certified dark sky places than any other country, with locations spread across the Southwest, the Appalachians, the Great Plains, and beyond.
            </p>
            <p>
              Finding dark skies doesn't always require a long trip. Even moderately dark skies (Bortle 3-4) offer dramatically better stargazing than suburban locations. Use our <Link to="/dark-sky-locations" className="text-gold hover:text-gold-light font-medium">dark sky location directory</Link> to find certified sites near you, or explore the map above to identify dark corridors within driving distance.
            </p>
          </div>
        </section>

        {/* Bortle Scale */}
        <section id="bortle-scale">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-3">
            The Bortle Scale: Measuring Night Sky Darkness
          </h2>
          <p className="text-muted-foreground mb-4 max-w-3xl">
            Created by amateur astronomer John Bortle in 2001, the Bortle Dark-Sky Scale is the standard way to describe night sky brightness. It ranges from Class 1 (the darkest skies attainable on Earth) to Class 9 (inner-city skies). Understanding the Bortle class of your location helps you set realistic expectations for stargazing and astrophotography.
          </p>
          <p className="text-muted-foreground mb-8 max-w-3xl">
            The colors on our light pollution map correspond directly to these Bortle classes. Use the <Link to="/planner" className="text-gold hover:text-gold-light font-medium">Milky Way Planner</Link> to see exactly what's visible from any Bortle class on any given night.
          </p>
          <div className="grid gap-3">
            {BORTLE_CLASSES.map((b) => (
              <div
                key={b.bortle}
                className="flex items-start gap-4 p-4 rounded-lg border border-border bg-card"
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg flex-shrink-0 border border-black/10"
                  style={{ backgroundColor: b.color, color: b.textColor }}
                >
                  {b.bortle}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    Class {b.bortle}: {b.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How to Find Dark Skies */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-4">
            How to Find Dark Skies Near You
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4 mb-8">
            <p>
              You don't need to travel to a remote desert to experience dark skies. With the right tools and a little planning, most people in the US can reach Bortle 3-4 skies within a few hours' drive. Here's how to plan your trip:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mb-3">
                <span className="text-gold font-bold">1</span>
              </div>
              <h3 className="font-semibold text-foreground mb-2">Check the Light Pollution Map</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Use the map above to identify dark corridors and certified dark sky sites within driving distance. Look for areas shown in black or dark blue (Bortle 1-3). Pay attention to the direction of nearby light domes, which will appear as bright spots on the horizon even from relatively dark locations.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mb-3">
                <span className="text-gold font-bold">2</span>
              </div>
              <h3 className="font-semibold text-foreground mb-2">Plan Around the Moon</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Even under perfectly dark skies, a bright moon washes out the Milky Way and faint deep-sky objects. Plan your trip within a few days of the new moon for the darkest skies. Our <Link to="/planner" className="text-gold hover:text-gold-light font-medium">Milky Way Planner</Link> shows moon phase, rise/set times, and a nightly visibility rating that accounts for moonlight.
              </p>
            </div>
            <div className="p-6 rounded-xl border border-border bg-card">
              <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mb-3">
                <span className="text-gold font-bold">3</span>
              </div>
              <h3 className="font-semibold text-foreground mb-2">Browse Dark Sky Locations</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our directory of <Link to="/dark-sky-locations" className="text-gold hover:text-gold-light font-medium">400+ certified dark sky locations</Link> includes Bortle ratings, best months for Milky Way visibility, peak galactic core altitude, and links to plan your shoot. Filter by state, Bortle class, or best viewing month to find the perfect destination.
              </p>
            </div>
          </div>
        </section>

        {/* Best Dark Sky Locations */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-3">
            Best Dark Sky Locations in the US
          </h2>
          <p className="text-muted-foreground mb-8 max-w-3xl">
            These International Dark Sky certified locations offer some of the
            best night skies in the country. Each has been evaluated for sky
            quality, accessibility, and dark sky protection.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURED_LOCATIONS.map((loc) => (
              <Link
                key={loc.slug}
                to={`/dark-sky-locations/${loc.slug}`}
                className="group p-5 rounded-xl border border-border bg-card hover:border-gold/30 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-foreground group-hover:text-gold transition-colors">
                    {loc.name}
                  </h3>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2"
                    style={{
                      backgroundColor:
                        BORTLE_CLASSES[loc.bortle - 1]?.color || '#666',
                      color:
                        BORTLE_CLASSES[loc.bortle - 1]?.textColor || 'white',
                    }}
                  >
                    Bortle {loc.bortle}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  <MapPin className="w-3.5 h-3.5 inline mr-1" />
                  {loc.state}
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  View Details <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/dark-sky-locations"
              className="text-sm font-semibold text-gold hover:text-gold-light transition-colors inline-flex items-center gap-1"
            >
              Browse all 400+ dark sky locations{' '}
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* CTA Cards */}
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link
              to="/dark-sky-locations"
              className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">
                  Find Dark Sky Sites
                </h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Browse 400+ certified dark sky locations with Bortle
                ratings, visibility data, and Milky Way calendars.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Browse Locations <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link
              to="/planner"
              className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Plan Your Shoot</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                See exactly when the Milky Way is visible from any
                location. Moon phase, darkness windows, and more.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Open Planner <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
            <Link
              to="/features"
              className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors"
            >
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-gold" />
                <h4 className="font-semibold text-white">Go Premium</h4>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-3">
                Weather forecasts, trip planning, PDF exports, and full
                annual calendars. Everything you need to plan.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                Learn More <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-foreground mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item) => (
              <details
                key={item.question}
                className="group border border-border rounded-lg bg-card"
              >
                <summary className="cursor-pointer px-6 py-4 font-semibold text-foreground list-none flex items-center justify-between">
                  {item.question}
                  <span className="text-muted-foreground group-open:rotate-180 transition-transform">
                    &#9662;
                  </span>
                </summary>
                <div className="px-6 pb-4 text-muted-foreground leading-relaxed">
                  {item.answer}
                </div>
              </details>
            ))}
          </div>
        </section>

      </div>

      {/* Newsletter — full width, outside the max-w container */}
      <NewsletterSignup className="max-w-full" />
    </>
  );
}
